
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// @google/genai-fix: Added Database to lucide-react imports.
import { Vote, Lock, ShieldCheck, BarChart3, Flame, Sparkles, Copy, CheckCircle, Database } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useWallet } from '../../hooks/useWallet';
import { GhostBrandIcon } from '../ui/PartnerIcons';
import { TokenIcon } from '../ui/ChainIcons';
import { GHOST_ICON_URL, VEGHOST_SUI_MINT } from '../../constants';

export const GovernanceView: React.FC = () => {
    const { balances, isConnected } = useWallet();
    const [lockAmount, setLockAmount] = useState('');
    const [isLocking, setIsLocking] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [copied, setCopied] = useState(false);

    const ghostBalance = useMemo(() => balances.find(b => b.symbol === 'GHOST')?.balance || 0, [balances]);
    const veGhostBalance = useMemo(() => balances.find(b => b.symbol === 'veGHOST')?.balance || 420.5, [balances]);

    const stats = [
        { label: "Total GHOST Locked", value: "84.2M", icon: Lock },
        { label: "Voting Power", value: `${veGhostBalance.toLocaleString()}`, icon: Vote },
        { label: "Treasury Split", value: "40%", icon: ShieldCheck },
        { label: "Next Quarterly Burn", value: "14d 08h", icon: Flame },
    ];

    const handleLock = async () => {
        setIsLocking(true);
        await new Promise(res => setTimeout(res, 2000));
        setIsLocking(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
    };

    const handleCopyMint = () => {
        navigator.clipboard.writeText(VEGHOST_SUI_MINT);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="space-y-8 animate-fade-in pb-12">
            <div className="flex flex-col md:flex-row justify-between items-end gap-4">
                <div>
                    <h2 className="font-serif text-3xl text-[#0A0B0F] dark:text-white font-bold">Ghost Governance</h2>
                    <p className="text-stone-500 text-sm max-w-md">Lock your GHOST to receive veGHOST. Earn voting rights and ecosystem rewards.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" icon={<BarChart3 size={14}/>}>DAO Proposals</Button>
                </div>
            </div>

            {/* Ecosystem Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                    <Card key={i} className="p-5 border-stone-100 dark:border-white/5 bg-white dark:bg-[#15171C]">
                        <div className="flex items-center gap-3 mb-2 text-stone-400">
                            <stat.icon size={16} />
                            <span className="text-[10px] font-bold uppercase tracking-widest">{stat.label}</span>
                        </div>
                        <div className="text-2xl font-serif font-bold text-[#0A0B0F] dark:text-white">{stat.value}</div>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Locking Interface */}
                <div className="lg:col-span-2">
                    <Card className="h-full border-nobel-gold/20 dark:border-nobel-gold/10 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
                            <GhostBrandIcon className="w-64 h-64 rotate-12" />
                        </div>
                        
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-14 h-14 bg-nobel-gold/10 rounded-2xl flex items-center justify-center text-nobel-gold shadow-inner border border-nobel-gold/20 relative">
                                    <TokenIcon symbol="veGHOST" fallback={GHOST_ICON_URL} className="w-10 h-10" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-serif font-bold text-[#0A0B0F] dark:text-white">Escrow Locking</h3>
                                    <div 
                                        onClick={handleCopyMint}
                                        className="inline-flex items-center gap-2 mt-1 cursor-pointer hover:text-nobel-gold transition-colors"
                                    >
                                        <span className="text-[10px] font-mono text-stone-500 uppercase tracking-tight">Mint: {VEGHOST_SUI_MINT.slice(0, 8)}...{VEGHOST_SUI_MINT.slice(-8)}</span>
                                        {copied ? <CheckCircle size={10} className="text-emerald-500" /> : <Copy size={10} className="text-stone-400" />}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center px-1">
                                        <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">Amount to Lock</label>
                                        <span className="text-[10px] font-mono text-stone-400">Available: {ghostBalance.toLocaleString()} GHOST</span>
                                    </div>
                                    <div className="relative">
                                        <input 
                                            type="number" 
                                            value={lockAmount}
                                            onChange={(e) => setLockAmount(e.target.value)}
                                            className="w-full p-4 pr-24 bg-stone-50 dark:bg-black/20 rounded-2xl border border-stone-200 dark:border-white/10 text-lg font-mono font-bold focus:ring-2 focus:ring-nobel-gold/30 outline-none dark:text-white"
                                            placeholder="0.00"
                                        />
                                        <button 
                                            onClick={() => setLockAmount(ghostBalance.toString())}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 px-3 py-1 bg-nobel-gold/20 text-nobel-gold rounded-lg text-[10px] font-bold hover:bg-nobel-gold/30"
                                        >
                                            MAX
                                        </button>
                                    </div>
                                </div>

                                <div className="p-4 bg-[#F9F8F4] dark:bg-white/5 rounded-2xl border border-stone-200 dark:border-white/5 space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-stone-500">Lock Duration</span>
                                        <span className="font-bold text-[#0A0B0F] dark:text-white">24 Months</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-stone-500">Estimated Yield</span>
                                        <span className="font-bold text-emerald-500">12.5% APY</span>
                                    </div>
                                    <div className="pt-3 border-t border-stone-200 dark:border-white/10 flex justify-between items-center">
                                        <span className="font-bold text-[#0A0B0F] dark:text-white">You will receive</span>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-lg font-bold text-nobel-gold">{lockAmount || '0'}</span>
                                            <span className="text-xs font-bold text-stone-400 uppercase">veGHOST</span>
                                        </div>
                                    </div>
                                </div>

                                <Button 
                                    onClick={handleLock}
                                    disabled={!isConnected || !lockAmount || parseFloat(lockAmount) <= 0}
                                    isLoading={isLocking}
                                    className="w-full h-14 rounded-2xl text-base shadow-xl"
                                >
                                    Confirm Lock & Delegate
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Ecosystem Insights */}
                <div className="space-y-6">
                    <Card className="bg-[#0A0B0F] text-white border-white/10">
                        <div className="flex items-center gap-3 mb-6">
                            <Flame size={20} className="text-orange-500" />
                            <h3 className="font-bold text-sm uppercase tracking-widest">Deflationary Engine</h3>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <div className="text-[10px] text-stone-500 font-bold uppercase tracking-widest mb-1">Total GHOST Burned</div>
                                <div className="text-3xl font-mono font-bold tracking-tighter">1,240,502 <span className="text-xs text-stone-500 uppercase">GHOST</span></div>
                            </div>
                            <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                                <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: "60%" }}
                                    className="h-full bg-orange-500"
                                />
                            </div>
                            <p className="text-[11px] text-stone-400 leading-relaxed italic">
                                60% of all transaction fees are permanently removed from supply. 40% is funneled to the Ghost DAO Treasury.
                            </p>
                        </div>
                    </Card>

                    <Card>
                        <div className="flex items-center gap-3 mb-4">
                            <Sparkles size={20} className="text-nobel-gold" />
                            <h3 className="font-bold text-[#0A0B0F] dark:text-white">Active Proposals</h3>
                        </div>
                        <div className="space-y-3">
                            {[
                                { title: "GP-04: Increase suiet-fee", votes: "84% Yes" },
                                { title: "GP-03: Titanium v2 support", votes: "99% Yes" },
                            ].map((p, i) => (
                                <div key={i} className="p-3 rounded-xl bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/10 flex justify-between items-center group cursor-pointer hover:border-nobel-gold/30">
                                    <div className="text-xs font-bold text-stone-800 dark:text-stone-300">{p.title}</div>
                                    <div className="text-[10px] font-mono text-emerald-500">{p.votes}</div>
                                </div>
                            ))}
                        </div>
                        <Button variant="ghost" size="sm" className="w-full mt-4 text-[10px] font-bold uppercase tracking-widest">View Governance Hub</Button>
                    </Card>
                </div>
            </div>

            <AnimatePresence>
                {showSuccess && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="fixed bottom-10 right-10 bg-[#0A0B0F] text-white px-8 py-5 rounded-[24px] shadow-2xl flex items-center gap-4 z-50 border border-nobel-gold/50">
                        <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                            <ShieldCheck size={14} className="text-white" strokeWidth={3} />
                        </div>
                        <div>
                            <div className="font-bold">GHOST Locked Successfully</div>
                            <div className="text-[10px] text-stone-400 font-mono">veGHOST minted & delegated.</div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
